import type { CartGroup, PricingData } from '@rpl/pricing';
import { useMemo, useState } from 'react';
import { formatMoney } from '../../lib/format';
import { frameColourOptions } from '../../lib/sizePaper';
import type { LocalPhoto, UiPrint } from '../../lib/types';
import { useStore } from '../../store/store';
import { ModalShell } from '../sizepaper/ModalShell';
import { AddFrameDialog } from './AddFrameDialog';
import {
  borderSplitCaption,
  distinctPhotoIds,
  groupCaption,
  groupTitle,
  setGroupQtyPlan,
  stepGroupQty,
} from './cartModel';
import { frameOptionsForGroup } from './frame/frameOptions';
import { PhotoQtyDialog, QtyOpenButton } from './PhotoQtyDialog';
import { PrintQtyDialog } from './PrintQtyDialog';
import { QtyStepper } from './QtyStepper';
import { ThumbStack } from './ThumbStack';

/**
 * One cart line — a loose size+paper group or a framed product group.
 * QUANTITY (round 7): lines covering ONE photo with ONE print keep the direct
 * stepper (cartModel.stepGroupQty last-row semantics — a loose line never
 * drops below 1 print, framed lines have an explicit "Remove frame"). Lines
 * spanning MULTIPLE photos open the per-photo quantity editor instead (any
 * tap on the qty control), so the customer chooses exactly which photo gets
 * more prints. ROUND 9: a single-photo line whose photo carries SEVERAL
 * member prints (border split) opens the per-print editor the same way —
 * stepping blindly would change an arbitrary print.
 * Round 6: every line gets a ✕ that removes the whole line (confirmed when
 * the line represents more than one print).
 */
export function GroupCard({
  group,
  prints,
  photosById,
  pricing,
  onReview,
}: {
  group: CartGroup;
  prints: UiPrint[];
  photosById: ReadonlyMap<string, LocalPhoto>;
  pricing: PricingData;
  /** Frame-upsell low-res handoff — owned by CartScreen, which outlives this
   *  card (a full conversion drops the loose group from totals.groups). */
  onReview: (printId: string) => void;
}) {
  const updatePrint = useStore((s) => s.updatePrint);
  const removePrint = useStore((s) => s.removePrint);
  const unframe = useStore((s) => s.unframe);
  const upsellColours = useMemo(() => frameColourOptions(pricing), [pricing]);
  /** Which swatch was tapped — preselects the colour in the dialog. */
  const [upsellColour, setUpsellColour] = useState<string | undefined>(undefined);
  const setStep = useStore((s) => s.setStep);
  const [framing, setFraming] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [qtyOpen, setQtyOpen] = useState(false);

  const members = prints; // already filtered by the parent
  const photoIds = distinctPhotoIds(members);
  const multiPhoto = photoIds.length > 1;
  // One photo, several member prints (border split) — a blind step would
  // change an arbitrary print, so the qty control opens the per-print editor.
  const multiPrint = !multiPhoto && members.length > 1;
  const framed = !!group.frameId;
  const title = groupTitle(group);
  const caption = groupCaption(group, photoIds.length);
  // Borders are price-neutral so the line stays merged — the caption reveals
  // any white/handling split ("· 1 with white border").
  const borderSplit = framed ? null : borderSplitCaption(members);
  // Any-paper upsell (owner 2026-07-26): the CTA shows whenever ANY framed
  // config exists for the group (the dialog offers the full range), and the
  // teaser quotes the true floor price (the list is size-sorted, not price-
  // sorted, so take the min).
  const frameOptions = framed ? [] : frameOptionsForGroup(pricing, group);
  const frameFromPrice = frameOptions.length
    ? Math.min(...frameOptions.map((o) => o.price))
    : null;

  function step(delta: 1 | -1) {
    const action = stepGroupQty(members, delta);
    if (action.kind === 'inc' || action.kind === 'dec') {
      updatePrint(action.printUiId, { qty: action.qty });
    } else if (action.kind === 'remove') {
      removePrint(action.printUiId);
    }
  }

  /** Direct entry: set the group to an exact quantity (same last-row semantics). */
  function setQty(next: number) {
    for (const patch of setGroupQtyPlan(members, next)) {
      if (patch.qty <= 0) removePrint(patch.printUiId);
      else updatePrint(patch.printUiId, { qty: patch.qty });
    }
  }

  /* "Remove frame" removes the FRAME, not the line (owner-reported bug
     2026-07-27: it called removePrint and deleted the whole thing, which is
     what the row's ✕ is for). store.unframe puts the print back to what it was
     before framing when convertToFramed recorded it. */
  function removeFrame() {
    for (const m of members) unframe(m.id);
  }

  /** Remove the whole line (all member prints); confirm for multi-print lines. */
  function removeLine() {
    for (const m of members) removePrint(m.id);
    setConfirmRemove(false);
  }

  function askRemove() {
    if (group.qty > 1) setConfirmRemove(true);
    else removeLine();
  }

  const decDisabled = stepGroupQty(members, -1).kind === 'none';

  return (
    <div className="gcard">
      <div className="gcard__row">
        {framed && <span className="gcard__frameicon" aria-hidden="true" />}
        <ThumbStack photoIds={photoIds} photosById={photosById} framed={framed} />
        <div className="gcard__info">
          <div className="gcard__title">{title}</div>
          <div className="gcard__caption">
            {caption}
            {borderSplit && <> · {borderSplit}</>}
            {group.bulkApplied && <span className="gcard__bulktag">bulk</span>}
          </div>
          <div className="gcard__links">
            {framed ? (
              <button type="button" className="gcard__link" onClick={removeFrame}>
                Remove frame
              </button>
            ) : (
              <button type="button" className="gcard__link" onClick={() => setStep(1)}>
                Edit selection →
              </button>
            )}
          </div>
        </div>
        {multiPhoto ? (
          // Multi-photo line: any tap opens the per-photo editor — no more
          // silently bumping whichever photo happened to be last.
          <QtyOpenButton qty={group.qty} label={title} onOpen={() => setQtyOpen(true)} />
        ) : multiPrint ? (
          // One photo, several prints: any tap opens the per-print editor so
          // the customer picks WHICH print to add or remove.
          <QtyOpenButton
            qty={group.qty}
            label={title}
            title="Choose which print to add or remove"
            ariaPrefix="Change quantities per print"
            onOpen={() => setQtyOpen(true)}
          />
        ) : (
          <QtyStepper
            value={group.qty}
            onInc={() => step(1)}
            onDec={() => step(-1)}
            onSet={setQty}
            decDisabled={decDisabled}
            label={title}
          />
        )}
        <span className="gcard__total">{formatMoney(group.lineTotal)}</span>
        <button
          type="button"
          className="gcard__remove"
          aria-label={`Remove ${title} from your cart`}
          title="Remove this line"
          onClick={askRemove}
        >
          ✕
        </button>
      </div>

      {qtyOpen &&
        (multiPrint ? (
          <PrintQtyDialog
            pricing={pricing}
            members={members}
            photo={photosById.get(photoIds[0]!)}
            onClose={() => setQtyOpen(false)}
          />
        ) : (
          <PhotoQtyDialog
            members={members}
            photosById={photosById}
            title={title}
            onClose={() => setQtyOpen(false)}
          />
        ))}

      {confirmRemove && (
        <ModalShell onClose={() => setConfirmRemove(false)} width={420} label="Remove cart line" className="confirm">
          <div className="confirm__body">
            <h3 className="confirm__title">
              Remove {group.qty} print{group.qty === 1 ? '' : 's'} from your cart?
            </h3>
            <p className="confirm__text">
              {title}. Your photos stay in your library, ready to set up again.
            </p>
            <div className="confirm__actions">
              <button type="button" className="btn btn--secondary" onClick={() => setConfirmRemove(false)}>
                Keep
              </button>
              <button type="button" className="btn btn--primary confirm__danger" onClick={removeLine}>
                Remove
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      {frameFromPrice != null && (
        <div className="upsell">
          {/* The swatches ARE the selector (owner 2026-07-28): the card renders
              before a colour is chosen, so tapping one both picks the colour and
              opens the dialog on it, rather than asking twice. */}
          <span className="upsell__swatches">
            {upsellColours.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`upsell__graphic upsell__graphic--${c.id}`}
                aria-label={`Add a ${c.label.toLowerCase()} timber frame`}
                onClick={() => {
                  setUpsellColour(c.id);
                  setFraming(true);
                }}
              >
                <span className="upsell__matt" />
              </button>
            ))}
          </span>
          <div className="upsell__copy">
            <strong>Add a timber frame.</strong> Solid Aussie timber, real glass, ready to
            hang.{' '}
            <span className="upsell__price">
              Framed from {formatMoney(frameFromPrice)} (print included).
            </span>
          </div>
          <button type="button" className="upsell__btn" onClick={() => setFraming(true)}>
            Add frame
          </button>
        </div>
      )}

      {framing && frameOptions.length > 0 && (
        <AddFrameDialog
          group={group}
          members={members}
          photosById={photosById}
          pricing={pricing}
          onClose={() => setFraming(false)}
          onReview={onReview}
          initialColour={upsellColour}
        />
      )}
    </div>
  );
}
